import { landmineNames } from '@mm/constants/landmine-names';
import { useFollowUpCRUDActions } from '@mm/state/actions/FollowUpCRUDActions';
import { useWidgetCRUDActions } from '@mm/state/actions/WidgetCRUDActions';
import { ButtonType } from '@mm/state/model/ButtonModel';
import {
  FollowUpChannel,
  FollowUpChannels,
  FollowUpKind,
  FollowUpModel,
  QqFollowUpModel,
} from '@mm/state/model/FollowUpModel';
import { WidgetTypes } from '@mm/state/model/WidgetModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { widgetsByDialogueSelector } from '@mm/state/selectors/widgetSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { ReactComponent as IGIcon } from 'assets/instagram.svg';
import { ReactComponent as FBIcon } from 'assets/messenger-fb-new.svg';
import { ReactComponent as SMSIcon } from 'assets/smartphone.svg';
import { ReactComponent as WebchatIcon } from 'assets/webchat-new.svg';
import classnames from 'classnames';
import { without } from 'lodash';
import pluralize from 'pluralize';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { CollapsibleContentContainerWithoutLocalState } from '../../../../../../../../common/components/CollapsibleContentContainer/CollapsibleContentContainerWithoutLocalState';
import { ErrorTooltip } from '../../../../../../../../common/components/ErrorTooltip/ErrorTooltip';
import NumberInput from '../../../../../../../../common/components/NumberInput/NumberInput';
import SelectorWithArrow from '../../../../../../../../common/components/SelectorWithArrow/SelectorWithArrow';
import Switch from '../../../../../../../../common/components/Switch/Switch';
import { useGreenCueMarkers } from '../../../../../../../../common/hooks/use-green-cue-markers';
import { StartBotsDialogueEditor } from '../../../../../../startBotPromoters/common/StartBotsDialogueEditor/StartBotsDialogueEditor';
import './GenericFollowUpItem.pcss';
import {
  PathGraphContext,
  useNewGraphInstance,
} from '../../../../../../../../common/widgets/common/PathGraph/PathGraph';

type Props = {
  kind: FollowUpKind;
  index: number;
  landmineName: keyof typeof landmineNames;
  headingNote: string;
  hideWidgetAdder: boolean;
  record: FollowUpModel<QqFollowUpModel>;
  active: boolean;
  thenSelector: React.ReactNode;
  onToggleActive: (record: FollowUpModel<QqFollowUpModel> | undefined) => void;
  bottomNote?: React.ReactNode;
  showTimeWarning?: boolean;
};

type Delimiter = {
  name: string;
  id: string;
  value: number;
  threshold: number;
  singular: string;
};

const excludedButtonTypes = [
  ButtonType.OPT_IN,
  ButtonType.PAGE,
  ButtonType.POSTBACK,
  ButtonType.HANDOVER,
];

const excludeTypes = [
  WidgetTypes.QUICK_QUESTION,
  WidgetTypes.QQ_OPTIONS,
  WidgetTypes.QQ_EMAIL,
  WidgetTypes.QQ_PHONE,
  WidgetTypes.QQ_INFO,
  WidgetTypes.OTN,
  WidgetTypes.FORM,
  WidgetTypes.NAVIGATE,
  WidgetTypes.TYPING,
  WidgetTypes.OPT_IN,
  WidgetTypes.SMS_OPT_IN_V2,
];

const intervalValueFunc = (value: number, delimiter: Delimiter) => {
  return value * delimiter.value;
};

const intervalErrorFunc = (value?: number) => {
  if (!value || value < secondsThresholdMin) {
    return ['Follow up interval should be at least 1 minute'];
  }
};

const intervalWarning = 'No follow up will be sent on Messenger after 24 hours';

const secondsThresholdMin = 60;
const secondsThresholdMax = 86400;

const delimiters: Delimiter[] = [
  { name: 'Hours', id: 'hours', value: 3600, singular: 'Hour', threshold: 24 },
  {
    name: 'Minutes',
    id: 'minutes',
    value: 60,
    singular: 'Minute',
    threshold: 1440,
  },
];

export const GenericFollowUpItem = (props: Props) => {
  const {
    record,
    active,
    onToggleActive,
    kind,
    headingNote,
    landmineName,
    hideWidgetAdder,
    bottomNote,
    thenSelector,
    index,
    showTimeWarning = true,
  } = props;
  const widgetCRUDActions = useWidgetCRUDActions();
  const followUpCRUDActions = useFollowUpCRUDActions();
  const chatbotId = useSelector(activeChatbotIdSelector);
  const widgets = useSelector((state: StoreState) =>
    widgetsByDialogueSelector(record.pageId, state.widget.entities),
  );
  const modalActions = useModalActions();
  const blockedActivation = !useSelector(combinedFeatureFlagsSelector)
    .automationRulesFollowUps;
  const featureFlags = useSelector(combinedFeatureFlagsSelector);
  const [intervalDelimiter, setIntervalDelimiter] = useState(
    record.interval % 3600 === 0 ? delimiters[0] : delimiters[1],
  );
  const [intervalValue, setIntervalValue] = useState(
    record.interval / intervalDelimiter.value,
  );
  const { isSmsActivated } = useGreenCueMarkers();
  const smsChannelSelected =
    record.platforms?.includes('sms') && isSmsActivated;

  useEffect(() => {
    if (active && widgets.length === 0) {
      widgetCRUDActions.retrieveAll(record.pageId);
    }
  }, [active]);

  const onToggleActivationLabel = () => {
    const checked = record.status === 'paused';
    const status = checked ? 'active' : 'paused';
    if (blockedActivation) {
      modalActions.openUniversalUpgradeLandmineModal({
        landmineName: landmineNames[landmineName],
        feature: 'automationRulesFollowUps',
      });
    } else {
      followUpCRUDActions.update(chatbotId, record.id, { status }, kind);
    }
  };

  const pathGraph = useNewGraphInstance(record?.pageId);

  const intervalValueSeconds = intervalValueFunc(
    intervalValue,
    intervalDelimiter,
  );
  const showWarning =
    showTimeWarning && intervalValueSeconds > secondsThresholdMax;
  const intervalError = intervalErrorFunc(intervalValueSeconds);
  const delimiterText = pluralize(intervalDelimiter.singular, intervalValue);
  const updateInterval = (value: number, delimiter: Delimiter) => {
    const newInterval = intervalValueFunc(value, delimiter);
    if (newInterval === record.interval || intervalErrorFunc(newInterval)) {
      return;
    }
    followUpCRUDActions.update(
      chatbotId,
      record.id,
      { interval: newInterval },
      kind,
    );
  };
  const onToggleChannel = (
    channel: FollowUpChannel,
    channelSelected: boolean,
  ) => {
    const newChannels = channelSelected
      ? without(record.platforms, channel)
      : [...record.platforms, channel];
    followUpCRUDActions.update(
      chatbotId,
      record.id,
      { platforms: newChannels },
      kind,
    );
  };

  const ChannelFilterButton = (props: {
    channel: FollowUpChannel;
    channelIcon: any;
    title: string;
  }) => {
    const { channel, channelIcon, title } = props;
    const channelSelected = record.platforms?.includes(channel);
    return (
      <div
        className={classnames('generic-follow-up-item__channel-filter-button', {
          'generic-follow-up-item__channel-filter-button--selected':
            channelSelected,
        })}
        onClick={() => onToggleChannel(channel, channelSelected)}
      >
        {React.createElement(channelIcon)}
        <div className="generic-follow-up-item__channel-filter-button-tooltip">
          {title}
        </div>
      </div>
    );
  };

  const renderContainerMenu = () => (
    <div
      className="generic-follow-up-item__menu"
      onClick={e => active && e.stopPropagation()}
    >
      <div className="generic-follow-up-item__menu-left">
        <span className="generic-follow-up-item__menu-label">
          {`${headingNote} `}
          {!active && `${intervalValue} ${delimiterText}`}
        </span>
        {active && (
          <div className="generic-follow-up-item__interval-configuration">
            <NumberInput
              className="generic-follow-up-item__interval-value-input"
              value={intervalValue}
              type="number"
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                const value = Number(e.target.value);
                setIntervalValue(value);
              }}
              onBlur={() => updateInterval(intervalValue, intervalDelimiter)}
            />
            <SelectorWithArrow
              className="generic-follow-up-item__delimiter-selector"
              selectedItem={intervalDelimiter}
              items={delimiters}
              onChange={(id: string) => {
                const delimiter = delimiters.find(d => d.id === id);
                if (delimiter === undefined) {
                  return;
                }
                setIntervalDelimiter(delimiter);
                updateInterval(intervalValue, delimiter);
              }}
            />
            {showWarning && (
              <div className="generic-follow-up-item__warning">
                <i className="far fas fa-exclamation-circle" />
                {intervalWarning}
              </div>
            )}
            <ErrorTooltip
              classPrefix="generic-follow-up-item"
              validation={intervalError}
            />
          </div>
        )}
      </div>
      <div className="generic-follow-up-item__menu-right">
        <div className="generic-follow-up-item__platforms">
          <ChannelFilterButton
            channel="instagram"
            channelIcon={IGIcon}
            title={FollowUpChannels.instagram}
          />
          <ChannelFilterButton
            channel="facebook"
            channelIcon={FBIcon}
            title={FollowUpChannels.facebook}
          />
          <ChannelFilterButton
            channel="webchat"
            channelIcon={WebchatIcon}
            title={FollowUpChannels.webchat}
          />
          <ChannelFilterButton
            channel="sms"
            channelIcon={SMSIcon}
            title={FollowUpChannels.sms}
          />
        </div>
        <Switch
          checked={record.status === 'active'}
          disabled={false}
          onChange={onToggleActivationLabel}
        />
      </div>
    </div>
  );

  return (
    <CollapsibleContentContainerWithoutLocalState
      expandedOnMount={true}
      disableExpand={false}
      containerName={renderContainerMenu()}
      className="generic-follow-up-item__container"
      useCrossIconStyle={true}
      isExpanded={active}
      toggleIsExpanded={value => onToggleActive(value ? record : undefined)}
    >
      <div className="generic-follow-up-item__content">
        <div className="generic-follow-up-item__then-selector-container">
          {thenSelector}
        </div>
        <PathGraphContext.Provider value={pathGraph}>
          <StartBotsDialogueEditor
            disabledButtonTypes={excludedButtonTypes}
            hideTopWidgetAdder={hideWidgetAdder}
            disableFirstWidgetDeleting={hideWidgetAdder}
            chatbotId={chatbotId}
            specificityId={index}
            widgets={widgets}
            dialogueId={record.pageId}
            isSmsRelated={smsChannelSelected}
            widgetsToExclude={excludeTypes}
            platforms={['facebook']}
          />
        </PathGraphContext.Provider>
        {bottomNote}
      </div>
    </CollapsibleContentContainerWithoutLocalState>
  );
};
